import {
  ArrowLeft,
  Mail,
  MapPin,
  Calendar,
  Edit2,
  Settings,
  Shield,
  BadgeCheck,
  Briefcase,
  Award,
  Clock,
  Phone,
  Building2,
  Users,
  Star,
  MessageSquare,
  Wallet,
  HeartHandshake,
  FileCheck,
  ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import MemberProfileService from "../services/APIs/MemberProfileService";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";

const Profile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const memberId = searchParams.get("memberId");
  const [imageError, setImageError] = useState(false);

  // Fetch member profile
  const { data: memberProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["memberProfile", memberId],
    queryFn: async () => {
      try {
        return await MemberProfileService.getMemberProfile();
      } catch (error) {
        console.error("Error fetching member profile:", error);
        return null;
      }
    },
    enabled: true,
    refetchOnWindowFocus: true,
  });

  // Get consultation and contract status from API data
  const isConsultationEnabled =
    memberProfile?.consultation === true ||
    memberProfile?.consultation === "true" ||
    (typeof memberProfile?.consultation === "string" &&
      memberProfile.consultation.toLowerCase() === "yes");

  const isContractEnabled =
    memberProfile?.contract === true ||
    memberProfile?.contract === "true" ||
    (typeof memberProfile?.contract === "string" &&
      memberProfile.contract.toLowerCase() === "yes");

  useEffect(() => {
    if (memberProfile) {
      // Reset image error when profile changes
      setImageError(false);
    }
  }, [memberProfile]);

  // Helper to format date
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return "N/A";
    }
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Helper to calculate years of service
  const calculateYearsOfService = (): number => {
    if (!memberProfile?.created_on) {
      return 0;
    }

    try {
      const startDate = new Date(memberProfile.created_on);
      const endDate = memberProfile.retirement_date
        ? new Date(memberProfile.retirement_date)
        : new Date(); // Use current date if no retirement date

      // Ensure end date is not before start date
      if (endDate < startDate) {
        return 0;
      }

      const years =
        (endDate.getTime() - startDate.getTime()) /
        (1000 * 60 * 60 * 24 * 365.25);
      return Math.floor(years);
    } catch {
      return 0;
    }
  };

  // Helper to get initials
  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    const first = firstName?.charAt(0)?.toUpperCase() ?? "";
    const last = lastName?.charAt(0)?.toUpperCase() ?? "";
    return first + last || "U";
  };

  // Helper to build profile avatar URL
  const getProfileAvatarUrl = (): string | null => {
    if (!memberProfile) {
      return null;
    }
    const profileImage = memberProfile.profilephoto ?? null;
    if (!profileImage || typeof profileImage !== "string") {
      return null;
    }
    try {
      new URL(profileImage);
      return profileImage;
    } catch {
      const imagePath = profileImage.startsWith("/")
        ? profileImage
        : `/${profileImage}`;
      const apiBaseUrl =
        import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";
      return `${apiBaseUrl}${imagePath}`;
    }
  };

  // Get profile data from API
  const profileName = memberProfile?.first_name
    ? `${memberProfile.first_name}${memberProfile.last_name ? ` ${memberProfile.last_name}` : ""}`
    : "User";

  const profileInitials = getInitials(
    memberProfile?.first_name,
    memberProfile?.last_name
  );

  const stats = {
    committees: memberProfile?.committee_count ?? 0,
    sopsOwned: memberProfile?.sop_count ?? memberProfile?.sops_owned ?? 0,
  };

  // Get gov_skill array from API
  const govSkills = memberProfile?.gov_skill ?? [];

  // Get certificate array from API
  const certificates = memberProfile?.certificate ?? [];

  // Helper to extract filename without extension from URL or string
  const getFileNameWithoutExtension = (urlOrString: string | any): string => {
    if (!urlOrString) {
      return "Unnamed Certification";
    }

    const url =
      typeof urlOrString === "string"
        ? urlOrString
        : (urlOrString.url ?? urlOrString.file_path ?? urlOrString.name ?? "");

    if (!url) {
      return "Unnamed Certification";
    }

    try {
      // Extract filename from URL
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      let filename = pathname.split("/").pop() ?? "";
      // Remove extension
      filename = filename.replace(/\.[^/.]+$/, "");
      // Remove the last part after the last underscore (usually the random ID)
      const lastUnderscoreIndex = filename.lastIndexOf("_");
      if (lastUnderscoreIndex !== -1) {
        filename = filename.substring(0, lastUnderscoreIndex);
      }
      // Replace underscores with spaces and capitalize each word
      return filename
        .split("_")
        .map(
          (word: string) =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    } catch {
      // If not a valid URL, try to extract from path string
      let filename = url.split("/").pop() ?? url;
      // Remove extension
      filename = filename.replace(/\.[^/.]+$/, "");
      // Remove the last part after the last underscore (usually the random ID)
      const lastUnderscoreIndex = filename.lastIndexOf("_");
      if (lastUnderscoreIndex !== -1) {
        filename = filename.substring(0, lastUnderscoreIndex);
      }
      // Replace underscores with spaces and capitalize each word
      return filename
        .split("_")
        .map(
          (word: string) =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    }
  };

  // Static retirement data (keep as is)
  const retirementInfo = {
    yearsOfService: 18,
    estimatedRetirementDate: "January 2030",
    pensionTier: "Tier 2 - Classic",
    vestingStatus: "Fully Vested",
    vestingProgress: 100,
    pensionCredits: 18.5,
    healthBenefitsEligible: true,
    retireeHealthPlan: "Premium Gold",
    postRetirementOptions: ["Advisory Role", "Contract Work", "Peer Mentoring"],
    beneficiaryDesignated: true,
    lastPensionReview: "October 2024",
  };

  // Helper to format date as relative time
  const formatRelativeTime = (
    dateString: string | null | undefined
  ): string => {
    if (!dateString) {
      return "N/A";
    }
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      const diffInSeconds = Math.floor(diffInMs / 1000);
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);
      const diffInWeeks = Math.floor(diffInDays / 7);
      const diffInMonths = Math.floor(diffInDays / 30);

      if (diffInSeconds < 60) {
        return "Just now";
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes} ${diffInMinutes === 1 ? "minute" : "minutes"} ago`;
      } else if (diffInHours < 24) {
        return `${diffInHours} ${diffInHours === 1 ? "hour" : "hours"} ago`;
      } else if (diffInDays < 7) {
        return `${diffInDays} ${diffInDays === 1 ? "day" : "days"} ago`;
      } else if (diffInWeeks < 4) {
        return `${diffInWeeks} ${diffInWeeks === 1 ? "week" : "weeks"} ago`;
      } else if (diffInMonths < 12) {
        return `${diffInMonths} ${diffInMonths === 1 ? "month" : "months"} ago`;
      } else {
        return formatDate(dateString);
      }
    } catch {
      return dateString;
    }
  };

  // Helper to map activity type to display type
  const mapActivityType = (activityType: string | null | undefined): string => {
    if (!activityType) {
      return "default";
    }
    const type = activityType.toLowerCase();
    if (type.includes("create") || type.includes("created")) {
      return "sop";
    }
    if (type.includes("update") || type.includes("updated")) {
      return "sop";
    }
    if (type.includes("delete") || type.includes("deleted")) {
      return "sop";
    }
    if (type.includes("certification") || type.includes("cert")) {
      return "certification";
    }
    if (type.includes("event") || type.includes("attended")) {
      return "event";
    }
    if (type.includes("consultation") || type.includes("consult")) {
      return "consultation";
    }
    if (type.includes("meeting")) {
      return "meeting";
    }
    if (type.includes("award") || type.includes("received")) {
      return "award";
    }
    return "default";
  };

  // Type for activity display
  interface ActivityDisplay {
    action: string;
    date: string;
    type: string;
    user_name?: string;
    created_on?: string;
  }

  // Get recent activities from API
  const recentActivity: ActivityDisplay[] = (
    memberProfile?.recent_activities ?? []
  ).map((activity: any) => ({
    action: activity.activity_title ?? "Activity",
    date: formatRelativeTime(activity.created_on),
    type: mapActivityType(activity.activity_type),
    user_name: activity.user_name,
    created_on: activity.created_on,
  }));

  if (isLoadingProfile) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(CitycollabNavigationRoutes.NEW_DESIGN)}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                  <h1 className="text-lg font-bold text-primary md:text-xl">
                    My Profile
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </header>
        <main className="container mx-auto px-4 py-6 md:py-8">
          <div className="py-12 text-center">
            <p className="text-muted-foreground">Loading profile...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!memberProfile) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(CitycollabNavigationRoutes.NEW_DESIGN)}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                  <h1 className="text-lg font-bold text-primary md:text-xl">
                    My Profile
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </header>
        <main className="container mx-auto px-4 py-6 md:py-8">
          <div className="py-12 text-center">
            <p className="text-destructive">Failed to load profile</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(CitycollabNavigationRoutes.NEW_DESIGN)}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold text-primary md:text-xl">
                  My Profile
                </h1>
                <p className="text-xs text-muted-foreground">
                  Manage your identity and skills
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(CitycollabNavigationRoutes.SETTINGS)}
              >
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 md:py-8">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left Column - Membership Card & Status */}
          <div className="space-y-6 lg:col-span-1">
            {/* Digital Membership Card */}
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
                <div className="mb-4 flex items-center justify-between">
                  <Shield className="h-8 w-8" />
                  <Badge
                    variant="secondary"
                    className="border-0 bg-white/20 text-white"
                  >
                    Active
                  </Badge>
                </div>
                <div className="mb-4 flex items-center gap-4">
                  <div className="relative">
                    {getProfileAvatarUrl() && !imageError ? (
                      <img
                        src={getProfileAvatarUrl() ?? ""}
                        alt={profileName}
                        className="h-20 w-20 rounded-full border-2 border-white/20 object-cover"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/20 text-3xl font-bold">
                        {profileInitials}
                      </div>
                    )}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{profileName}</h2>
                    <p className="text-sm opacity-90">
                      {memberProfile.position ?? ""}
                    </p>
                    <p className="text-xs opacity-75">
                      {memberProfile.department ?? ""}
                    </p>
                  </div>
                </div>
                <div className="mt-4 border-t border-white/20 pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs opacity-75">Member ID</p>
                      <p className="font-mono font-medium">
                        {memberProfile.member_id ?? "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs opacity-75">Since</p>
                      <p className="font-medium">
                        {formatDate(memberProfile.created_on)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <CardContent className="p-4 pt-5">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  <span>{memberProfile.organization ?? "N/A"}</span>
                </div>
                <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>{memberProfile.address ?? "N/A"}</span>
                </div>
              </CardContent>
            </Card>

            {/* Engagement Status */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Engagement Status</CardTitle>
                <CardDescription className="text-xs">
                  Opt-in to peer engagement features
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-primary" />
                    <Label htmlFor="consultation" className="text-sm">
                      Open to Peer Consultation
                    </Label>
                  </div>
                  <Switch
                    id="consultation"
                    checked={isConsultationEnabled}
                    disabled
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-primary" />
                    <Label htmlFor="contract" className="text-sm">
                      Available for Contract
                    </Label>
                  </div>
                  <Switch id="contract" checked={isContractEnabled} disabled />
                </div>
                {(isConsultationEnabled || isContractEnabled) && (
                  <div className="border-t pt-2">
                    <div className="flex flex-wrap gap-1.5">
                      {isConsultationEnabled && (
                        <Badge
                          variant="outline"
                          className="border-blue-500/30 bg-blue-500/10 text-blue-600"
                        >
                          <MessageSquare className="mr-1 h-3 w-3" />
                          Peer Consultation
                        </Badge>
                      )}
                      {isContractEnabled && (
                        <Badge
                          variant="outline"
                          className="border-green-500/30 bg-green-500/10 text-green-600"
                        >
                          <Briefcase className="mr-1 h-3 w-3" />
                          Contract Work
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Profile Stats</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-muted/50 p-3 text-center">
                    <p className="text-2xl font-bold text-primary">
                      {stats.committees}
                    </p>
                    <p className="text-xs text-muted-foreground">Committees</p>
                  </div>
                  <div
                    className="cursor-pointer rounded-lg bg-muted/50 p-3 text-center transition-colors hover:bg-muted"
                    onClick={() => navigate(CitycollabNavigationRoutes.SOP_LIBRARY)}
                  >
                    <p className="text-2xl font-bold text-primary">
                      {stats.sopsOwned}
                    </p>
                    <p className="text-xs text-muted-foreground">SOPs Owned</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Tabs Content */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="mb-6 grid h-auto w-full grid-cols-3 gap-1 md:grid-cols-5">
                <TabsTrigger
                  value="details"
                  className="py-2 text-xs md:text-sm"
                >
                  Details
                </TabsTrigger>
                <TabsTrigger
                  value="retirement"
                  className="py-2 text-xs md:text-sm"
                >
                  Retirement
                </TabsTrigger>
                <TabsTrigger value="skills" className="py-2 text-xs md:text-sm">
                  Skills
                </TabsTrigger>
                <TabsTrigger
                  value="social"
                  className="col-span-1 py-2 text-xs md:text-sm"
                >
                  Social
                </TabsTrigger>
                <TabsTrigger
                  value="certifications"
                  className="col-span-2 py-2 text-xs md:col-span-1 md:text-sm"
                >
                  Certs
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-6">
                {/* Contact Information */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Contact Information</CardTitle>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(CitycollabNavigationRoutes.SETTINGS)}
                      >
                        <Edit2 className="mr-2 h-4 w-4" />
                        Edit
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="flex items-center gap-3 rounded-lg bg-muted/30 p-3">
                        <Mail className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Email</p>
                          <p className="text-sm font-medium">
                            {memberProfile.email ?? "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 rounded-lg bg-muted/30 p-3">
                        <Phone className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Phone</p>
                          <p className="text-sm font-medium">
                            {memberProfile.phonenumber ?? "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 rounded-lg bg-muted/30 p-3">
                        <MapPin className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Location
                          </p>
                          <p className="text-sm font-medium">
                            {memberProfile.address ?? "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 rounded-lg bg-muted/30 p-3">
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Organization
                          </p>
                          <p className="text-sm font-medium">
                            {memberProfile.organization ?? "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {recentActivity.length > 0 ? (
                        recentActivity.map((activity: ActivityDisplay) => (
                          <div
                            key={`${activity.user_name ?? "unknown"}-${activity.action}-${activity.created_on ?? activity.date}`}
                            className="flex items-start gap-3 border-b pb-3 last:border-0 last:pb-0"
                          >
                            <div
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${activity.type === "certification"
                                ? "bg-emerald-500/10 text-emerald-600"
                                : activity.type === "event"
                                  ? "bg-blue-500/10 text-blue-600"
                                  : activity.type === "sop"
                                    ? "bg-amber-500/10 text-amber-600"
                                    : activity.type === "consultation"
                                      ? "bg-purple-500/10 text-purple-600"
                                      : activity.type === "award"
                                        ? "bg-pink-500/10 text-pink-600"
                                        : "bg-primary/10 text-primary"
                                }`}
                            >
                              {activity.type === "certification" && (
                                <Award className="h-4 w-4" />
                              )}
                              {activity.type === "event" && (
                                <Calendar className="h-4 w-4" />
                              )}
                              {activity.type === "sop" && (
                                <Shield className="h-4 w-4" />
                              )}
                              {activity.type === "consultation" && (
                                <MessageSquare className="h-4 w-4" />
                              )}
                              {activity.type === "meeting" && (
                                <Users className="h-4 w-4" />
                              )}
                              {activity.type === "award" && (
                                <Star className="h-4 w-4" />
                              )}
                              {activity.type === "default" && (
                                <Clock className="h-4 w-4" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium">
                                {activity.action}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {activity.date}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="py-4 text-center text-sm text-muted-foreground">
                          No recent activities available
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Professional Identity Portability */}
              </TabsContent>

              <TabsContent value="retirement" className="space-y-6">
                {/* Retirement Overview */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Retirement Overview</CardTitle>
                        <CardDescription>
                          Your pension and retirement benefits status
                        </CardDescription>
                      </div>
                      <Badge
                        variant="outline"
                        className="border-emerald-500/30 bg-emerald-500/10 text-emerald-600"
                      >
                        <BadgeCheck className="mr-1 h-3.5 w-3.5" />
                        {retirementInfo.vestingStatus}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="rounded-lg bg-muted/30 p-4">
                        <div className="mb-2 flex items-center gap-2">
                          <Clock className="h-4 w-4 text-primary" />
                          <p className="text-sm font-medium">
                            Years of Service
                          </p>
                        </div>
                        <p className="text-3xl font-bold text-primary">
                          {calculateYearsOfService()}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Pension Credits: {retirementInfo.pensionCredits}
                        </p>
                      </div>
                      <div className="rounded-lg bg-muted/30 p-4">
                        <div className="mb-2 flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-primary" />
                          <p className="text-sm font-medium">
                            Est. Retirement Date
                          </p>
                        </div>
                        <p className="text-2xl font-bold text-foreground">
                          {memberProfile?.retirement_date
                            ? formatDate(memberProfile.retirement_date)
                            : "Not Set"}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Tier: {retirementInfo.pensionTier}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Health Benefits */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <HeartHandshake className="h-5 w-5" />
                      Health Benefits
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between rounded-lg bg-muted/30 p-3">
                        <div>
                          <p className="text-sm font-medium">
                            Retiree Health Eligible
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Upon retirement, you qualify for continued health
                            coverage
                          </p>
                        </div>
                        <Badge
                          variant={
                            retirementInfo.healthBenefitsEligible
                              ? "default"
                              : "outline"
                          }
                          className={
                            retirementInfo.healthBenefitsEligible
                              ? "bg-emerald-500"
                              : ""
                          }
                        >
                          {retirementInfo.healthBenefitsEligible
                            ? "Eligible"
                            : "Not Eligible"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between rounded-lg bg-muted/30 p-3">
                        <div>
                          <p className="text-sm font-medium">
                            Projected Health Plan
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Based on your current tier and service
                          </p>
                        </div>
                        <Badge variant="outline">
                          {retirementInfo.retireeHealthPlan}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between rounded-lg bg-muted/30 p-3">
                        <div>
                          <p className="text-sm font-medium">
                            Beneficiary Designated
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Ensure your benefits are properly assigned
                          </p>
                        </div>
                        <Badge
                          variant={
                            retirementInfo.beneficiaryDesignated
                              ? "default"
                              : "outline"
                          }
                          className={
                            retirementInfo.beneficiaryDesignated
                              ? "bg-emerald-500"
                              : "border-red-500/30 bg-red-500/10 text-red-600"
                          }
                        >
                          {retirementInfo.beneficiaryDesignated
                            ? "Complete"
                            : "Action Needed"}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Post-Retirement Options */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Wallet className="h-5 w-5" />
                      Post-Retirement Opportunities
                    </CardTitle>
                    <CardDescription>
                      Ways you can continue to contribute after retirement
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3 md:grid-cols-3">
                      {retirementInfo.postRetirementOptions.map(option => (
                        <div
                          key={option}
                          className="cursor-pointer rounded-lg border p-4 text-center transition-colors hover:border-primary/50 hover:bg-primary/5"
                        >
                          <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                            {option === "Advisory Role" && (
                              <Users className="h-5 w-5 text-primary" />
                            )}
                            {option === "Contract Work" && (
                              <Briefcase className="h-5 w-5 text-primary" />
                            )}
                            {option === "Peer Mentoring" && (
                              <MessageSquare className="h-5 w-5 text-primary" />
                            )}
                          </div>
                          <p className="text-sm font-medium">{option}</p>
                        </div>
                      ))}
                    </div>
                    <Button
                      className="mt-4 w-full"
                      variant="outline"
                      onClick={() => navigate("/retiree-marketplace")}
                    >
                      <FileCheck className="mr-2 h-4 w-4" />
                      View Retiree Marketplace
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="skills" className="space-y-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Skills</CardTitle>
                        <CardDescription>
                          Your government skills
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {govSkills.length > 0 ? (
                        govSkills.map((skill: any) => (
                          <Badge
                            key={skill.id ?? skill.skill}
                            variant="secondary"
                            className="px-3 py-1.5 text-sm"
                          >
                            {skill.skill ?? skill.name ?? "Unknown Skill"}
                          </Badge>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No skills available
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="social" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Social Media</CardTitle>
                    <CardDescription>
                      Connect on other platforms
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {memberProfile.whatsapp && (
                      <a
                        href={`https://wa.me/${memberProfile.whatsapp.replace(/\s/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-4 rounded-lg border p-4 transition-colors hover:bg-muted/50"
                      >
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
                          <svg
                            className="h-6 w-6 text-green-600"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">WhatsApp</p>
                          <p className="text-sm text-muted-foreground">
                            {memberProfile.whatsapp}
                          </p>
                        </div>
                        <ExternalLink className="h-5 w-5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                      </a>
                    )}

                    {memberProfile.twitter && (
                      <a
                        href={`https://twitter.com/${memberProfile.twitter.replace("@", "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-4 rounded-lg border p-4 transition-colors hover:bg-muted/50"
                      >
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-500/10">
                          <svg
                            className="h-6 w-6 text-sky-500"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">Twitter / X</p>
                          <p className="text-sm text-muted-foreground">
                            {memberProfile.twitter}
                          </p>
                        </div>
                        <ExternalLink className="h-5 w-5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                      </a>
                    )}

                    {memberProfile.facebook && (
                      <a
                        href={`https://facebook.com/${memberProfile.facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-4 rounded-lg border p-4 transition-colors hover:bg-muted/50"
                      >
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-600/10">
                          <svg
                            className="h-6 w-6 text-blue-600"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">Facebook</p>
                          <p className="text-sm text-muted-foreground">
                            {memberProfile.facebook}
                          </p>
                        </div>
                        <ExternalLink className="h-5 w-5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                      </a>
                    )}

                    {memberProfile.linkedin && (
                      <a
                        href={`https://linkedin.com/in/${memberProfile.linkedin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-4 rounded-lg border p-4 transition-colors hover:bg-muted/50"
                      >
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-700/10">
                          <svg
                            className="h-6 w-6 text-blue-700"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">LinkedIn</p>
                          <p className="text-sm text-muted-foreground">
                            {memberProfile.linkedin}
                          </p>
                        </div>
                        <ExternalLink className="h-5 w-5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                      </a>
                    )}

                    {!memberProfile.whatsapp &&
                      !memberProfile.twitter &&
                      !memberProfile.facebook &&
                      !memberProfile.linkedin && (
                        <p className="py-4 text-center text-sm text-muted-foreground">
                          No social media links available
                        </p>
                      )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="certifications" className="space-y-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Certifications & Credentials</CardTitle>
                        <CardDescription>
                          Your verified professional certifications
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {certificates.length > 0 ? (
                        certificates.map((cert: any, index: number) => (
                          <div
                            key={cert.id ?? cert.name ?? index}
                            className="flex items-center gap-4 rounded-lg border p-4"
                          >
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
                              <Award className="h-6 w-6 text-emerald-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-medium">
                                  {cert.name ??
                                    cert.title ??
                                    getFileNameWithoutExtension(cert)}
                                </h4>
                                <BadgeCheck className="h-4 w-4 text-emerald-500" />
                              </div>
                              {cert.issuer && cert.year && (
                                <p className="text-sm text-muted-foreground">
                                  {cert.issuer} â€¢ {cert.year}
                                </p>
                              )}
                            </div>
                            <Badge variant="default" className="bg-emerald-500">
                              Verified
                            </Badge>
                          </div>
                        ))
                      ) : (
                        <p className="py-4 text-center text-sm text-muted-foreground">
                          No certifications available
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
